import React from "react";

import { NumberInput } from "@impero-com/common/components/form/fields/numberInput";
import { SelectInput } from "@impero-com/common/components/form/fields/selectInput";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { FormBox, FormLabel } from "@impero-com/ui";
import { Control } from "react-hook-form";
import * as Yup from "yup";

import {
    OffsetAnchor,
    OffsetType,
    DueDateOffsetForMonthOffsetPeriod as MonthDueDateOffset,
    ControlRecurrenceRule,
    CalendarView,
} from "api/generated/model";
import { CalendarSelectInput } from "components/calendarSelectInput/calendarSelectInput";

import { ControlViewFormSchema } from "../formSchema";

import * as theme from "./MonthDueDateOffsetInput.theme.css";
import * as styles from "./styles.css";
import { TimeAndTimezone } from "./TimeAndTimezone";

export const getMonthDueDateOffsetSchema = (t: TFunction) =>
    Yup.object({
        offsetPeriod: Yup.object({
            months: Yup.number()
                .integer()
                .min(0)
                .max(99)
                .defined()
                .required(t("Validation_Required")),
            days: Yup.number()
                .integer()
                .min(0)
                .max(30)
                .defined()
                .required(t("Validation_Required")),
        }).defined(),
        anchor: Yup.string()
            .oneOf(["StartOfInterval", "EndOfInterval"])
            .defined(),
        offsetType: Yup.string()
            .oneOf(["BeforeAnchor", "AfterAnchor"])
            .defined(),
    }).defined();

export type RecurrenceType = Exclude<
    ControlRecurrenceRule["type"],
    "Date" | "Weekly"
>;

export interface DefaultMonthDueDateOffsetInputProps {
    label?: React.ReactNode;
    control: Control<ControlViewFormSchema>;
    value: MonthDueDateOffset;
    onChange: (value: MonthDueDateOffset) => void;
    calendarId: number | null;
    onCalendarIdChange: (value: number | null) => void;
    recurrenceType: RecurrenceType;
    initialCalendars: CalendarView[];
    disabled?: boolean;
}

export const DefaultMonthDueDateOffsetInput = (
    props: DefaultMonthDueDateOffsetInputProps
) => {
    return <InnerMonthDueDateOffsetInput {...props} isDefault={true} />;
};

export interface CustomMonthDueDateOffsetInputProps {
    label?: React.ReactNode;
    control: Control<ControlViewFormSchema>;
    value: MonthDueDateOffset;
    onChange: (value: MonthDueDateOffset) => void;
    calendarId: number | null;
    recurrenceType: RecurrenceType;
    initialCalendars: CalendarView[];
    disabled?: boolean;
}

export const CustomMonthDueDateOffsetInput = (
    props: CustomMonthDueDateOffsetInputProps
) => {
    return (
        <InnerMonthDueDateOffsetInput
            {...props}
            onCalendarIdChange={() => {}}
            isDefault={false}
        />
    );
};

interface InnerMonthDueDateOffsetInputProps extends DefaultMonthDueDateOffsetInputProps {
    isDefault: boolean;
}

const InnerMonthDueDateOffsetInput = ({
    label,
    control,
    value,
    onChange,
    calendarId,
    onCalendarIdChange,
    recurrenceType,
    initialCalendars,
    disabled,
    isDefault,
}: InnerMonthDueDateOffsetInputProps) => {
    const { t } = useTranslation();

    const defaultFieldsDisabled = disabled || !isDefault;

    // Display days + 1 in the UI (1-indexed: "day 1" = offset 0)
    const displayDays = value.offsetPeriod.days + 1;

    const handleMonthsChange = (months: number | null) => {
        onChange({
            ...value,
            offsetPeriod: { ...value.offsetPeriod, months: months ?? 0 },
        });
    };

    const handleDaysChange = (daysPlusOne: number | null) => {
        onChange({
            ...value,
            offsetPeriod: {
                ...value.offsetPeriod,
                days: (daysPlusOne ?? 1) - 1,
            },
        });
    };

    const handleOffsetTypeChange = (
        offsetType: string | number | null | undefined
    ) => {
        onChange({ ...value, offsetType: offsetType as OffsetType });
    };

    const handleAnchorChange = (anchor: string | number | null | undefined) => {
        onChange({ ...value, anchor: anchor as OffsetAnchor });
    };

    const periodNameMap: Record<RecurrenceType, string> = {
        Monthly: t("CreateControl_Recurrence_DateRule_OfTheMonth"),
        EveryTwoMonths: t("CreateControl_Recurrence_DateRule_OfTheTwoMonth"),
        Quarterly: t("CreateControl_Recurrence_DateRule_OfTheQuarter"),
        HalfYearly: t("CreateControl_Recurrence_DateRule_OfTheHalf"),
        Yearly: t("CreateControl_Recurrence_DateRule_OfTheYear"),
    };

    return (
        <FormBox>
            {label && <FormLabel>{label}</FormLabel>}
            <div className={theme.monthDueDateOffset}>
                <div>
                    <NumberInput
                        value={value.offsetPeriod.months}
                        onChange={handleMonthsChange}
                        disabled={disabled}
                        min={0}
                        max={99}
                    />
                </div>
                <div>{t("CreateControl_Recurrence_DateRule_MonthsAnd")}</div>
                <div>
                    <NumberInput
                        value={displayDays}
                        onChange={handleDaysChange}
                        disabled={disabled}
                        className={styles.inputWithAddonAfter}
                        min={1}
                        max={31}
                        addonAfter={
                            <CalendarSelectInput
                                value={calendarId}
                                onChange={onCalendarIdChange}
                                initialCalendars={initialCalendars}
                                disabled={defaultFieldsDisabled}
                            />
                        }
                    />
                </div>
                <div>
                    <SelectInput
                        value={value.offsetType}
                        onChange={handleOffsetTypeChange}
                        onBlur={() => {}}
                        options={[
                            {
                                value: "BeforeAnchor",
                                label: t(
                                    "CreateControl_Recurrence_DateRule_Before"
                                ),
                            },
                            {
                                value: "AfterAnchor",
                                label: t(
                                    "CreateControl_Recurrence_DateRule_After"
                                ),
                            },
                        ]}
                        disabled={disabled}
                    />
                </div>
                <div>{t("CreateControl_Recurrence_DateRule_TheAnchor")}</div>
                <div>
                    <SelectInput
                        value={value.anchor}
                        onChange={(v) => handleAnchorChange(v)}
                        onBlur={() => {}}
                        options={[
                            {
                                value: "StartOfInterval",
                                label: t(
                                    "CreateControl_Recurrence_DateRule_Beginning"
                                ),
                            },
                            {
                                value: "EndOfInterval",
                                label: t(
                                    "CreateControl_Recurrence_DateRule_End"
                                ),
                            },
                        ]}
                        disabled={disabled}
                    />
                </div>
                <div>
                    {periodNameMap[recurrenceType]}{" "}
                    {t("CreateControl_Recurrence_At")}
                </div>
                <TimeAndTimezone
                    control={control}
                    disabled={defaultFieldsDisabled}
                />
            </div>
        </FormBox>
    );
};
